import React from 'react';
import { isoToday } from '../data/mockCalendarData';
import { sessionsApi } from '../api/sessionsApi';
import { uiBus } from 'shared/api/uiBus';
import { extractApiError } from 'shared/errorUtils';

export function useSessionFlows({ fetchWeekly }) {
  // empty cell + create
  const [emptySlot, setEmptySlot] = React.useState(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);

  const openCreateAt = React.useCallback((slot) => {
    setEmptySlot(slot);
    setCreateOpen(true);
  }, []);

  const handleEmptyCellClick = React.useCallback((slot) => {
    setEmptySlot(slot);
    setSheetOpen(true);
  }, []);

  // ✅ selected (snapshot + id)
  const [selectedSession, setSelectedSession] = React.useState(null);
  const [selectedSessionId, setSelectedSessionId] = React.useState(null);

  // ✅ detail
  const [detailOpen, setDetailOpen] = React.useState(false);
  const openDetail = React.useCallback((s) => {
    setSelectedSession(s); // snapshot
    setSelectedSessionId(s?.id ?? null); // ✅ id
    setDetailOpen(true);
  }, []);

  // ✅ edit
  const [editOpen, setEditOpen] = React.useState(false);
  const openEdit = React.useCallback((s) => {
    setSelectedSession(s);
    setSelectedSessionId(s?.id ?? null); // ✅ id
    setEditOpen(true);
  }, []);
  const [deleteError, setDeleteError] = React.useState(null);

  // delete confirm
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deletingSession, setDeletingSession] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const requestDelete = React.useCallback((s) => {
    setDeletingSession(s);
    setDeleteError(null); // ✅
    setDeleteConfirmOpen(true);
  }, []);

  const cancelDelete = React.useCallback(() => {
    if (deleting) return;
    setDeleteConfirmOpen(false);
    setDeletingSession(null);
    setDeleteError(null); // ✅
  }, [deleting]);

  const confirmDelete = React.useCallback(async () => {
    if (!deletingSession?.id) {
      setDeleteConfirmOpen(false);
      setDeletingSession(null);
      setDeleteError(null);
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      await sessionsApi.deleteSession(deletingSession.id);

      uiBus.emit('toast', { type: 'success', message: 'Seans başarıyla silindi.' });

      setDeleteConfirmOpen(false);
      setDeletingSession(null);
      setDeleteError(null);
      await fetchWeekly?.();
    } catch (e) {
      console.error('[sessions] delete failed', e);

      const apiErr = extractApiError(e);
      setDeleteError(apiErr);

      // toast opsiyonel (istersen kapat)
      uiBus.emit('toast', { type: 'error', message: apiErr?.message || 'Silme başarısız.' });
    } finally {
      setDeleting(false);
    }
  }, [deletingSession, fetchWeekly]);

  // callbacks for modals
  const onCreated = React.useCallback(async () => {
    setCreateOpen(false);
    setSheetOpen(false);
    await fetchWeekly?.();
  }, [fetchWeekly]);

  const onUpdated = React.useCallback(async () => {
    setEditOpen(false);
    await fetchWeekly?.();
  }, [fetchWeekly]);

  // quick add from header button
  const addDefault = React.useCallback(() => {
    setEmptySlot({ dayISO: isoToday(), startMin: 9 * 60 });
    setCreateOpen(true);
  }, []);

  return {
    // empty cell / create
    deleteError,

    emptySlot,
    setEmptySlot,
    sheetOpen,
    setSheetOpen,
    createOpen,
    setCreateOpen,
    handleEmptyCellClick,
    openCreateAt,
    addDefault,

    // selected + detail + edit
    selectedSession,
    setSelectedSession,
    selectedSessionId,
    setSelectedSessionId,

    detailOpen,
    setDetailOpen,
    openDetail,

    editOpen,
    setEditOpen,
    openEdit,

    // delete
    deleteConfirmOpen,
    deletingSession,
    deleting,
    requestDelete,
    cancelDelete,
    confirmDelete,

    // modal callbacks
    onCreated,
    onUpdated,
  };
}
